import type { PageSideNavDivider, PageSideNavLink } from "@octopusdeploy/design-system-components";
import { Callout, Level2OuterPageLayout, Level2OuterPageLoading, useIsLargerThanIpadResolution } from "@octopusdeploy/design-system-components";
import { CodeBranchIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { GitRefResource, PlatformHubVersionControlSettingsResource, ProjectTemplateResource, Repository } from "@octopusdeploy/octopus-server-client";
import { ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useReducer, useRef } from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import { repository } from "~/clientInstance";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import type { ProjectTemplateContextProps, ProjectTemplateVersion } from "~/routing/pageRegistrations/ProjectTemplatePageRegistration";
import type { ProjectTemplateStatus } from "./ProjectTemplateStatus";
import { initialProjectTemplateStatusState, ProjectTemplateStatusBar, projectTemplateStatusReducer } from "./ProjectTemplateStatus";
import { ProjectTemplateStatusContextProvider } from "./ProjectTemplateStatusContext";
export interface ProjectTemplateLayoutProps {
    slug: string;
    gitRef: string;
    children: (params: {
        projectTemplateContext: ProjectTemplateContextProps;
        projectTemplateStatus: ProjectTemplateStatus;
    }) => React.ReactNode;
}
export function ProjectTemplateLayout({ slug, gitRef, children }: ProjectTemplateLayoutProps) {
    const { navigate } = useSpaceAwareNavigation();
    const { result: persistenceSettings, error: persistenceSettingsError } = useInlineStatusQuery((repository: Repository) => repository.PlatformHub.getVersionControlSettings(), [], "Get platform hub version control settings");
    const { result: gitRefValidationResult, error: gitRefValidationResultError } = useInlineStatusQuery((repository: Repository) => repository.PlatformHub.validateGitRef(gitRef), [gitRef], "Validate platform hub git ref");
    const setGitRef = (newGitRef: string) => {
        if (persistenceSettings) {
            lastAccessedPlatformHubGitRef.save(newGitRef, persistenceSettings);
        }
        navigate(links.projectTemplateDeploymentProcessPage.generateUrl({ slug, gitRef: newGitRef }));
    };
    if (!persistenceSettings || !gitRefValidationResult) {
        const errorInfo = persistenceSettingsError ? [createErrorInfoFromError(persistenceSettingsError)] : gitRefValidationResultError ? [createErrorInfoFromError(gitRefValidationResultError)] : undefined;
        return (<main>
                <Level2OuterPageLoading errors={errorInfo}/>
            </main>);
    }
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return <PlatformHubVcsError error={gitRefValidationResult} gitRef={gitRef} persistenceSettings={persistenceSettings} setGitRef={setGitRef}/>;
    }
    return (<GitValidatedProjectTemplateLayout gitRef={gitRef} slug={slug} persistenceSettings={persistenceSettings} gitRefResource={gitRefValidationResult.GitRef}>
            {children}
        </GitValidatedProjectTemplateLayout>);
}
interface GitValidatedProjectTemplateLayoutProps extends ProjectTemplateLayoutProps {
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefResource: GitRefResource | undefined;
}
export function GitValidatedProjectTemplateLayout({ slug, gitRef, children, persistenceSettings, gitRefResource }: GitValidatedProjectTemplateLayoutProps) {
    const createPageSideNavLink = useCreatePageSideNavLink();
    const [projectTemplateStatusState, projectTemplateStatusDispatch] = useReducer(projectTemplateStatusReducer, initialProjectTemplateStatusState);
    const refetchProjectTemplateStatus = useRef<(() => void) | undefined>(undefined);
    const onRefetchReady = React.useCallback((refetch: () => void) => {
        refetchProjectTemplateStatus.current = refetch;
    }, []);
    const { result: projectTemplate, error: projectTemplateError, refetch } = useInlineStatusQuery((repository: Repository) => repository.ProjectTemplates.get(slug, gitRef), [slug, gitRef], "Get project template");
    const { result: publishedVersions, error: projectTemplateVersionsError, refetch: refetchPublishedVersions, } = useInlineStatusQuery<ProjectTemplateVersion[]>((repository) => repository.ProjectTemplates.getVersions(slug).then((r) => r.ProjectTemplateVersions.map((v) => ({ Version: v.Version, IsPreRelease: v.IsPreRelease }))), [slug], "Get project template versions");
    const { result: projectTemplateLink } = useInlineStatusQuery((repository: Repository) => repository.ProjectTemplates.getLink(slug, gitRef), [slug, gitRef], "Get project template link");
    const getProjectTemplateLogo = (projectTemplate: ProjectTemplateResource) => {
        const iconUrl = repository.Icons.getIconUrl(projectTemplate.Icon);
        return iconUrl ? { href: iconUrl, accessibleName: `${projectTemplate.Name} icon` } : undefined;
    };
    if (!projectTemplate || !publishedVersions) {
        const errorInfo = projectTemplateError ? [createErrorInfoFromError(projectTemplateError)] : projectTemplateVersionsError ? [createErrorInfoFromError(projectTemplateVersionsError)] : undefined;
        return (<main>
                <Level2OuterPageLoading errors={errorInfo}/>
            </main>);
    }
    const navigationLinks: (PageSideNavLink | PageSideNavDivider)[] = [];
    navigationLinks.push(createPageSideNavLink("Process", links.projectTemplateDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef }), true));
    navigationLinks.push("divider");
    navigationLinks.push(createPageSideNavLink("Variables", links.projectTemplateVariablesPage.generateUrl({ slug: projectTemplate.Slug, gitRef }), true));
    navigationLinks.push(createPageSideNavLink("Parameters", links.projectTemplateBranchParametersPage.generateUrl({ slug: projectTemplate.Slug, gitRef }), true));
    navigationLinks.push("divider");
    navigationLinks.push(createPageSideNavLink("Versions", links.projectTemplateVersionsPage.generateUrl({ slug: projectTemplate.Slug, gitRef }), true));
    navigationLinks.push("divider");
    const projectTemplateContext: ProjectTemplateContextProps = {
        projectTemplate,
        persistenceSettings,
        gitRefResource,
        projectTemplateLink,
        publishedVersions,
        refetchPublishedVersions,
    };
    const projectTemplateStatus = {
        state: projectTemplateStatusState,
        dispatch: projectTemplateStatusDispatch,
        refetch: refetchProjectTemplateStatus.current,
    };
    return (<main>
            <Level2OuterPageLayout navItems={navigationLinks} header={{
            breadcrumbs: [{ label: "Project Templates", pageUrl: links.projectTemplatesPage.generateUrl() }],
            title: projectTemplate.Name,
            logo: getProjectTemplateLogo(projectTemplate),
            titleIcon: <ProjectTemplatePageHeaderTitleIcon />,
            statusSection: <ProjectTemplateStatusBar slug={slug} gitRef={gitRef} state={projectTemplateStatusState} dispatch={projectTemplateStatusDispatch} enableLinks onRefetchReady={onRefetchReady}/>,
        }}>
                <ProjectTemplateStatusContextProvider value={projectTemplateStatus}>
                    {children({
            projectTemplateContext,
            projectTemplateStatus,
        })}
                </ProjectTemplateStatusContextProvider>
            </Level2OuterPageLayout>
        </main>);
}
function ProjectTemplatePageHeaderTitleIcon() {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return <>{isLargerThanIpad ? <CodeBranchIcon size={20} color={themeTokens.color.icon.tertiary}/> : undefined}</>;
}
interface GetProjectTemplateInitialSuccessCalloutArgs {
    showInitialSuccessCallout: boolean;
    setShowInitialSuccessCallout: (value: boolean) => void;
    projectTemplateName: string;
}
export function GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName }: GetProjectTemplateInitialSuccessCalloutArgs): PageContentCalloutType | undefined {
    if (showInitialSuccessCallout) {
        return {
            type: "custom",
            content: (<Callout type={"success"} title={`"${projectTemplateName}" successfully committed, published and shared.`} canClose={true} onClose={() => setShowInitialSuccessCallout(false)}>
                    This template can now be used when creating a project.
                </Callout>),
        };
    }
    return undefined;
}
